import { useCallback, useEffect, useRef, useState } from "react";
import { Account, Location, ZuoraPaymentMethod } from "@/api/opb/opbTypes";
import { opbApi } from "@/api/opb/opbApi";
import {
  changeDefaultPaymentMethod,
  isExpiredCard,
  removeSavedPaymentMethod,
} from "./utils/payment-method-settings";

interface AccountPayments {
  account: Account;
  methods: ZuoraPaymentMethod[];
}

export function usePaymentMethods(accountId?: string): {
  accounts: Account[];
  locations: Location[];
  account?: Account;
  methods: ZuoraPaymentMethod[];
  isLoading: boolean;
  isBusy: boolean;
  error?: Error;
  mutationError?: Error;
  refresh: () => Promise<void>;
  mutate: (action: (account: Account) => Promise<void>) => Promise<boolean>;
  makeDefault: (id: string) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  setAutoPay: (value: boolean) => Promise<boolean>;
} {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [payments, setPayments] = useState<AccountPayments>();
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<Error>();
  const [mutationError, setMutationError] = useState<Error>();
  const currentId = useRef(accountId);
  const mutationPending = useRef(false);
  const readVersion = useRef(0);
  const mounted = useRef(true);
  currentId.current = accountId;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await opbApi.fetchCurrentUserAccounts();
        const ids = [
          ...new Set(
            data.map((account) => account.locationId).filter((id) => id != null)
          ),
        ];
        const accountLocations =
          ids.length > 0
            ? await opbApi.fetchLocations({ locationIds: ids })
            : [];
        if (!cancelled) {
          setAccounts(data);
          setLocations(accountLocations);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError(
            new Error(
              "We couldn’t load your billing accounts. Please try again."
            )
          );
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (accountId == null) return;
    readVersion.current += 1;
    const requestVersion = readVersion.current;
    const [account, { data: methods }] = await Promise.all([
      opbApi.fetchAccount(accountId),
      opbApi.fetchPaymentMethods(accountId),
    ]);
    if (
      mounted.current &&
      currentId.current === accountId &&
      requestVersion === readVersion.current
    ) {
      setPayments({ account, methods });
      setAccounts((previous) =>
        previous.map((value) =>
          value._id === accountId ? { ...value, ...account } : value
        )
      );
    }
  }, [accountId]);

  useEffect(() => {
    let cancelled = false;
    setPayments(undefined);
    setMutationError(undefined);
    if (accountId == null || accounts.length === 0) return undefined;
    if (!accounts.some((account) => account._id === accountId)) {
      setError(new Error("Account not found"));
      setIsLoading(false);
      return undefined;
    }
    setIsLoading(true);
    setError(undefined);
    refresh()
      .catch(() => {
        if (!cancelled)
          setError(
            new Error("Couldn’t load payment methods. Please try again.")
          );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, accounts.length, refresh]);

  const mutate = async (
    action: (account: Account) => Promise<void>
  ): Promise<boolean> => {
    if (
      error != null ||
      currentId.current !== accountId ||
      mutationPending.current ||
      payments == null ||
      payments.account._id !== accountId ||
      accountId == null
    )
      return false;
    mutationPending.current = true;
    setIsBusy(true);
    setMutationError(undefined);
    let completed = false;
    try {
      await action(payments.account);
      completed = true;
      await refresh();
      return currentId.current === accountId && mounted.current;
    } catch {
      if (mounted.current && currentId.current === accountId) {
        setMutationError(
          new Error(
            completed
              ? "Your change was saved, but we couldn’t refresh the page. Please reload before making more changes."
              : "We couldn’t complete all changes. Some settings may have been saved. Check the current settings and try again."
          )
        );
        try {
          await refresh();
        } catch {
          setError(
            new Error("Couldn’t load payment methods. Please try again.")
          );
        }
      }
      return false;
    } finally {
      mutationPending.current = false;
      if (mounted.current) setIsBusy(false);
    }
  };

  return {
    accounts,
    locations,
    account:
      payments != null && payments.account._id === accountId
        ? payments.account
        : undefined,
    methods:
      payments != null && payments.account._id === accountId
        ? payments.methods
        : [],
    isLoading,
    isBusy,
    error,
    mutationError,
    refresh,
    mutate,
    makeDefault: (id) =>
      mutate((account) => changeDefaultPaymentMethod(opbApi, account._id, id)),
    remove: (id) =>
      mutate((account) => removeSavedPaymentMethod(opbApi, account._id, id)),
    setAutoPay: (value) =>
      mutate(async (account) => {
        if (value && !account.defaultPaymentMethodId)
          throw new Error("A default payment method is required.");
        const defaultMethod = payments?.methods.find(
          (method) => method.id === account.defaultPaymentMethodId
        );
        if (value && defaultMethod != null && isExpiredCard(defaultMethod))
          throw new Error(
            "Update the card expiration date before enabling Auto-Pay."
          );
        await opbApi.updateUserAccount({
          accountId: account._id,
          autoPay: value,
        });
        const updated = await opbApi.fetchAccount(account._id);
        if (updated.autopay !== value)
          throw new Error("Auto-Pay update could not be confirmed.");
      }),
  };
}
